import { useMemo } from "react";

import { Divider, Grid } from "@mui/material";
import { FormikTextField, FormikZipCodeField } from "@shared";

import { SystemDropdownCategory } from "@/api";
import { useClientsSearch } from "@/common/components/clients-search/components/clients-search-dialog/hooks/useClientsSearch";
import { FormikCounselorSelect } from "@/common/components/counselor-select/FormikCounselorSelect";
import { FormikAutocomplete } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { FormikDatePicker } from "@/common/components/fields/formik/formik-date-picker/FormikDatePicker";
import { FormDialog } from "@/common/components/form-dialog/FormDialog";
import { useCourts } from "@/common/hooks/useCourts";
import { useSystemDropdownOptions } from "@/common/hooks/useSystemDropdownOptions";
import { ClientsFilterType } from "@/pages/clients/clients/Clients.types";
import { useTranslation } from "@/translations";

interface Props {
  initialFilters: ClientsFilterType | null;
  onSearch: (filters: ClientsFilterType) => Promise<void>;
  onClose: () => void;
}

export function ClientsSearchDialog({ initialFilters, onSearch, onClose }: Props) {
  const { t } = useTranslation();
  const { formik, dropdownOptions } = useClientsSearch(initialFilters, onSearch, onClose);
  const { optionItems: statusOptions } = useSystemDropdownOptions(SystemDropdownCategory.ClientStatus);
  const { courts } = useCourts();

  const courtOptions = useMemo(
    () => courts.map((court) => ({ id: court.id, label: court.name })).sort((a, b) => a.label.localeCompare(b.label)),
    [courts]
  );

  return (
    <FormDialog
      title={t("clients.search.dialogTitle")}
      onClose={onClose}
      formik={formik}
      submitLabel={t("clients.search.showMatchingResults")}
      confirmDirtyClose={false}
    >
      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid size={4}>
          <FormikAutocomplete
            formik={formik}
            fieldName="salutation"
            label={t("clients.search.salutation")}
            options={dropdownOptions.salutations}
          />
        </Grid>
        <Grid size={4}>
          <FormikAutocomplete
            formik={formik}
            fieldName="title"
            label={t("clients.search.title")}
            options={dropdownOptions.titles}
          />
        </Grid>
        <Grid size={4}>
          <FormikTextField formik={formik} fieldName="name" label={t("clients.search.name")} />
        </Grid>

        <Grid size={4}>
          <FormikTextField
            formik={formik}
            fieldName="insuranceNumber"
            label={t("clients.search.socialSecurityNumber")}
          />
        </Grid>
        <Grid size={4}>
          <FormikDatePicker formik={formik} fieldName="birthday" label={t("clients.search.birthday")} clearable />
        </Grid>
        <Grid size={4}>
          <FormikTextField formik={formik} fieldName="caseNumber" label={t("clients.search.idNumber")} />
        </Grid>

        <Grid size={12}>
          <Divider />
        </Grid>

        <Grid size={4}>
          <FormikTextField formik={formik} fieldName="street" label={t("clients.search.street")} />
        </Grid>
        <Grid size={4}>
          <FormikZipCodeField formik={formik} fieldName="zipCode" label={t("clients.search.zipCode")} type="text" />
        </Grid>
        <Grid size={4}>
          <FormikTextField formik={formik} fieldName="city" label={t("clients.search.city")} />
        </Grid>

        <Grid size={12}>
          <Divider />
        </Grid>

        <Grid size={4}>
          <FormikAutocomplete
            formik={formik}
            fieldName="nationality"
            label={t("clients.search.nationality")}
            options={dropdownOptions.citizenships}
          />
        </Grid>
        <Grid size={4}>
          <FormikAutocomplete
            formik={formik}
            fieldName="mediaInquiry"
            label={t("clients.search.mediaInquiry")}
            options={dropdownOptions.mediaInquiries}
          />
        </Grid>

        <Grid size={12}>
          <Divider />
        </Grid>

        <Grid size={4}>
          <FormikTextField formik={formik} fieldName="srvFileNumber" label={t("clients.search.srvFileNumber")} />
        </Grid>
        <Grid size={4}>
          <FormikAutocompleteSelect
            formik={formik}
            fieldName="courtId"
            label={t("clients.search.court")}
            options={courtOptions}
          />
        </Grid>

        <Grid size={12}>
          <Divider />
        </Grid>

        <Grid size={4}>
          <FormikDatePicker
            formik={formik}
            fieldName="caseNoteDateFrom"
            label={t("clients.search.caseNoteDateFrom")}
            clearable
          />
        </Grid>
        <Grid size={4}>
          <FormikDatePicker
            formik={formik}
            fieldName="caseNoteDateTo"
            label={t("clients.search.caseNoteDateTo")}
            clearable
          />
        </Grid>
        <Grid size={4}>
          <FormikTextField formik={formik} fieldName="caseNoteKeyword" label={t("clients.search.caseNoteKeyword")} />
        </Grid>

        <Grid size={12}>
          <Divider />
        </Grid>

        <Grid size={4}>
          <FormikCounselorSelect formik={formik} fieldName="assignedCounselorId" label={t("common.counselor")} />
        </Grid>
        <Grid size={4}>
          <FormikAutocompleteSelect
            formik={formik}
            fieldName="status"
            label={t("clients.search.currentStatus")}
            options={statusOptions}
          />
        </Grid>
      </Grid>
    </FormDialog>
  );
}
